/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: import.meta.env.VITE_APP_AUTH_CLIENT_ID,
        authority: "https://login.microsoftonline.com/common/",
        redirectUri: import.meta.env.VITE_APP_AUTH_REDIRECT_URI
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            }
        }
    }
};

// scope=User.Read is equivalent to https://graph.microsoft.com/User.Read
const graphScopes = ["User.Read", "User.ReadBasic.All", "People.Read", "Presence.Read.All"];

const settingsScope = [import.meta.env.VITE_APP_API_SCOPE];

const graphDefaultScope = ["https://graph.microsoft.com/.default"];

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...graphDefaultScope]
};

export const settingsRequest = {
    scopes: [...settingsScope]
};

export const peoplePickerRequest = {
    scopes: [...graphScopes]
};

export const groupPickerRequest = {
    scopes: ["GroupMember.Read.All"]
};

export const graphRequest = {
    scopes: [...graphScopes]
};
